import { InternalRoleSchema, Permission, Permissions, RoleSchema } from '@buyerflo/core/schemas/roles'
import { FrontendUserSchema } from '@buyerflo/core/schemas/userMembership'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    Building2,
    Check,
    ChevronDown,
    FileText,
    Settings as SettingsIcon,
    Trash2,
    Users,
} from '@tamagui/lucide-icons-2'
import { useMutation } from '@tanstack/react-query'
import { useRolesApi, useUsersApi } from 'apiProviders'
import Button from 'components/button'
import { Drawer } from 'components/drawer'
import { SlotInput } from 'components/input'
import { useShowSnack } from 'components/snackbar'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { SizableText, Switch, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'

interface PermGroup {
    name: string
    icon: typeof Users
    perms: { key: Permission; danger?: boolean }[]
}

const PERMISSION_GROUPS: PermGroup[] = [
    {
        name: 'User Management',
        icon: Users,
        perms: [
            { key: 'roles.edit' },
            { key: 'users.view' },
            { key: 'users.edit' },
            { key: 'conjunctionalAgencies.view' },
            { key: 'users.delete', danger: true },
        ],
    },
    {
        name: 'Clients',
        icon: Building2,
        perms: [
            { key: 'purchasers.view' },
            { key: 'purchasers.viewOthers' },
            { key: 'purchasers.edit' },
            { key: 'purchasers.fullControl' },
            { key: 'purchasers.delete', danger: true },
        ],
    },
    {
        name: 'Statements & Payroll',
        icon: FileText,
        perms: [
            { key: 'agentStatement.view' },
            { key: 'agentStatement.edit' },
            { key: 'agentStatement.generatePayroll' },
        ],
    },
    {
        name: 'Workspace',
        icon: SettingsIcon,
        perms: [
            { key: 'settings.view' },
            { key: 'settings.edit' },
            { key: 'todos.view' },
            { key: 'todos.manage' },
            { key: 'salesBoard.view' },
            { key: 'chat.manage' },
            { key: 'groups.edit' },
            { key: 'links.view' },
            { key: 'links.edit' },
            { key: 'calendar.view' },
            { key: 'calendar.edit' },
        ],
    },
]

const TOTAL_PERMS = Object.keys(Permissions).length

function ToggleRow({
    label,
    value,
    onChange,
    danger,
}: {
    label: string
    value: boolean
    onChange: (next: boolean) => void
    danger?: boolean
}) {
    return (
        <XStack
            alignItems="center"
            gap="$3"
            paddingVertical="$2"
            cursor="pointer"
            onPress={() => onChange(!value)}
        >
            <Switch
                size="$2"
                checked={value}
                onCheckedChange={onChange}
                {...(danger && value ? { theme: 'red' } : {})}
            >
                <Switch.Thumb animation="quick" />
            </Switch>
            <SizableText
                size="$2"
                color={danger ? '$statusDanger' : '$color'}
            >
                {label}
            </SizableText>
        </XStack>
    )
}

export function RoleDrawer({
    role,
    users,
    onClose,
}: {
    role: {} | InternalRoleSchema
    users: FrontendUserSchema[]
    onClose: () => void
}) {
    const trpc = useTRPC()
    const rolesApi = useRolesApi()
    const usersApi = useUsersApi()
    const showSnack = useShowSnack()
    const isNew = (role as InternalRoleSchema).id == undefined

    const createRoleMutation = useMutation(trpc.roles.createRole.mutationOptions())
    const updateRoleMutation = useMutation(trpc.roles.updateRole.mutationOptions())
    const deleteRoleMutation = useMutation(trpc.roles.deleteRole.mutationOptions())

    const { control, handleSubmit, watch, setValue } = useForm<RoleSchema>({
        defaultValues: { roleName: '', rolePermissions: [], ...role },
        resolver: zodResolver(RoleSchema),
    })

    const perms = watch('rolePermissions') ?? []
    const has = (key: Permission) => perms.includes(key)
    const toggle = (key: Permission, next: boolean) =>
        setValue('rolePermissions', next ? [...perms, key] : perms.filter(p => p !== key), {
            shouldDirty: true,
        })

    const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
    const toggleCollapsed = (name: string) =>
        setCollapsed(prev => {
            const next = new Set(prev)
            if (next.has(name)) next.delete(name)
            else next.add(name)
            return next
        })

    const usersInRole = users.filter(u => u.roleId === (role as InternalRoleSchema).id).length

    const onSubmit = handleSubmit(async data => {
        try {
            if (isNew) {
                await createRoleMutation.mutateAsync(data)
            } else {
                await updateRoleMutation.mutateAsync({ id: (role as InternalRoleSchema).id, data })
            }
            await Promise.all([rolesApi.refresh(), usersApi.refresh()])
            showSnack(`Role ${isNew ? 'created' : 'saved'}`, 'success')
            onClose()
        } catch (e) {
            console.error(e)
            showSnack('Error saving role', 'danger')
        }
    })

    return (
        <Drawer
            open
            onClose={onClose}
            title={isNew ? 'New Role' : 'Edit Role'}
            subtitle={`${perms.length} of ${TOTAL_PERMS} permissions enabled`}
            width={560}
            footer={
                <>
                    {!isNew ? (
                        <Button
                            variant="outlined"
                            theme="red"
                            icon={<Trash2 size={15} />}
                            disabled={usersInRole > 0}
                            onPress={async () => {
                                try {
                                    await deleteRoleMutation.mutateAsync({ id: (role as InternalRoleSchema).id })
                                    await rolesApi.refresh()
                                    showSnack('Role deleted', 'success')
                                    onClose()
                                } catch (e) {
                                    console.error(e)
                                    showSnack('Error deleting role', 'danger')
                                }
                            }}
                        >
                            Delete
                        </Button>
                    ) : (
                        <View />
                    )}
                    <XStack gap="$2">
                        <Button
                            variant="outlined"
                            onPress={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            theme="primary"
                            icon={<Check size={15} />}
                            onPress={onSubmit}
                        >
                            Save
                        </Button>
                    </XStack>
                </>
            }
        >
            <YStack gap="$5">
                <Controller
                    name="roleName"
                    control={control}
                    render={field => (
                        <SlotInput
                            {...field}
                            label="Role Name"
                        />
                    )}
                />

                <YStack gap="$3">
                    <SizableText
                        fontSize={11}
                        fontWeight="500"
                        letterSpacing={0.4}
                        textTransform="uppercase"
                        color="$colorMuted"
                    >
                        Permissions
                    </SizableText>

                    {PERMISSION_GROUPS.map(group => {
                        const Icon = group.icon
                        const enabled = group.perms.filter(p => has(p.key)).length
                        const total = group.perms.length
                        const allOn = enabled === total
                        const isCollapsed = collapsed.has(group.name)
                        return (
                            <YStack
                                key={group.name}
                                borderWidth={1}
                                borderColor="$borderColor"
                                borderRadius="$4"
                                overflow="hidden"
                            >
                                <XStack
                                    alignItems="center"
                                    gap="$2.5"
                                    paddingHorizontal="$3"
                                    paddingVertical="$2.5"
                                    backgroundColor="$backgroundStrong"
                                    cursor="pointer"
                                    onPress={() => toggleCollapsed(group.name)}
                                >
                                    <ChevronDown
                                        size={14}
                                        color="$colorMuted"
                                        rotate={isCollapsed ? '-90deg' : '0deg'}
                                    />
                                    <View
                                        width={26}
                                        height={26}
                                        borderRadius="$3"
                                        backgroundColor="$accentSoft"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Icon
                                            size={13}
                                            color="$accentText"
                                        />
                                    </View>
                                    <SizableText
                                        flex={1}
                                        size="$2"
                                        fontWeight="600"
                                    >
                                        {group.name}
                                    </SizableText>
                                    <SizableText
                                        style={{ fontSize: 11 }}
                                        fontFamily="$mono"
                                        color={allOn ? '$accentText' : '$colorMuted'}
                                    >
                                        {enabled}/{total}
                                    </SizableText>
                                    <Button
                                        size="$2"
                                        chromeless
                                        paddingHorizontal="$2"
                                        height={26}
                                        onPress={e => {
                                            e.stopPropagation()
                                            const keys = group.perms.map(p => p.key)
                                            if (allOn) {
                                                setValue(
                                                    'rolePermissions',
                                                    perms.filter(p => !keys.includes(p)),
                                                    { shouldDirty: true },
                                                )
                                            } else {
                                                setValue(
                                                    'rolePermissions',
                                                    Array.from(new Set([...perms, ...keys])),
                                                    { shouldDirty: true },
                                                )
                                            }
                                        }}
                                    >
                                        <SizableText
                                            fontSize={11}
                                            color="$colorMuted"
                                        >
                                            {allOn ? 'Clear' : 'All'}
                                        </SizableText>
                                    </Button>
                                </XStack>
                                {!isCollapsed && (
                                    <YStack
                                        paddingHorizontal="$3"
                                        paddingVertical="$1"
                                        gap="$0.5"
                                    >
                                        {group.perms.map(p => (
                                            <ToggleRow
                                                key={p.key}
                                                label={Permissions[p.key]}
                                                value={has(p.key)}
                                                danger={p.danger}
                                                onChange={next => toggle(p.key, next)}
                                            />
                                        ))}
                                    </YStack>
                                )}
                            </YStack>
                        )
                    })}
                </YStack>
            </YStack>
        </Drawer>
    )
}
